import { useMemo } from 'react';
import type { ElementType, ReactNode } from 'react';
import {
    Box,
    HStack,
    Icon,
    IconButton,
    Switch,
    Text,
    Tooltip,
    Tr,
    VStack,
} from '@chakra-ui/react';
import { TbChevronRight, TbInfoCircle } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import {
    PARTBAY_BLUE,
    SettingsCard,
    SettingsInlineNumberEdit,
    SettingsRow,
    SettingsRowActions,
    SettingsSubTable,
    SettingsTd,
    SettingsTh,
    useSubTablePagination,
} from '../../../../components/settings/primitives';
import PartBayButton from '../../../../components/PartBayButton';
import SearchableSingleSelect from '../../../../components/SearchableSingleSelect';
import {
    applyLkqPricingRuleChange,
    formatLkqPricingSubCompanySummary,
    groupLkqPricingRules,
    lkqPricingRuleCompanies,
    removeLkqPricingRule,
    type LkqPricingRule,
} from '../../../../utils/lkqPricingRows';
import { InlinePricingStatesCell, InlinePricingVendorCell } from './pricing/PricingInlineCells';
import {
    PRICING_TABLES,
    percentageOf,
    pricingEntriesOf,
    pricingEntryFactory,
    pricingRowPath,
    pricingUpdate,
} from './pricing/pricingTables';
import {
    AMOUNT_MISMATCH_DIRECTION_OPTIONS,
    CREDITED_AMOUNT_THRESHOLD_DESCRIPTION,
    DEFAULT_RECEIVED_BASE_THRESHOLD,
    DEFAULT_RECEIVED_PERCENT_THRESHOLD,
    DEFAULT_RETURNED_BASE_THRESHOLD,
    DEFAULT_RETURNED_PERCENT_THRESHOLD,
    RECEIVED_AMOUNT_THRESHOLD_DESCRIPTION,
    amountMismatchDirectionValue,
    amountMismatchDollarThreshold,
    amountMismatchThresholdHelp,
    flagBothDirectionsFromSelection,
    decimalToWholePercent,
    useInvoicingDraft,
    usePricingRowNames,
    useSupplierNames,
    wholePercentToDecimal,
} from './shared';
import type { InvoicingSectionProps } from './types';
import { AmountMismatchThresholdFields } from './AmountMismatchThresholdFields';
import {
    vendorFlaggingInvoiceTypeLabel,
    vendorFlaggingRuleKey,
    vendorFlaggingRulePath,
    vendorFlaggingRuleSummary,
} from '../../../../utils/vendorFlaggingRules';

/** The Flags section owns one pricing table: the discount flag expectations. */
const DISCOUNT_FLAG_SPEC = PRICING_TABLES['invoicing-flags'][0];
const readExpectedDiscount = percentageOf(DISCOUNT_FLAG_SPEC);

function InfoTip({ label, ariaLabel }: { label: ReactNode; ariaLabel: string }) {
    return (
        <Tooltip
            label={label}
            placement="top"
            hasArrow
            bg="gray.700"
            color="white"
            fontSize="sm"
            borderRadius="md"
            px={3}
            py={2}
            maxW="400px"
        >
            <IconButton
                aria-label={ariaLabel}
                icon={<Icon as={TbInfoCircle as ElementType} />}
                size="xs"
                variant="ghost"
                color="gray.500"
                _hover={{ color: 'gray.700' }}
            />
        </Tooltip>
    );
}

const FlagsSection = ({ data, organizationStates }: InvoicingSectionProps) => {
    const { invoicing, updateInvoicing, isSaving } = useInvoicingDraft(data);
    const navigate = useNavigate();

    const serverExpectations = useMemo(
        () => data.discountFlagExpectations ?? [],
        [data.discountFlagExpectations],
    );
    const { withNames, rememberNames } = usePricingRowNames(serverExpectations);
    const serverFlaggingRules = useMemo(
        () => data.vendorFlaggingRules ?? [],
        [data.vendorFlaggingRules],
    );
    const { supplierName } = useSupplierNames(serverFlaggingRules);

    const discountFlagExpectations = useMemo(
        () => withNames(pricingEntriesOf(DISCOUNT_FLAG_SPEC, invoicing)),
        [withNames, invoicing],
    );
    const discountFlagRules = useMemo(
        () => groupLkqPricingRules(discountFlagExpectations, readExpectedDiscount),
        [discountFlagExpectations],
    );

    const discountFlagPaging = useSubTablePagination(discountFlagRules);

    const vendorFlaggingRules = useMemo(
        () => invoicing.vendorFlaggingRules ?? [],
        [invoicing.vendorFlaggingRules],
    );
    const sortedVendorFlaggingRules = useMemo(
        () =>
            [...vendorFlaggingRules].sort((a, b) => {
                const vendorCmp = supplierName(a.supplierId).localeCompare(
                    supplierName(b.supplierId),
                    undefined,
                    { sensitivity: 'base' },
                );
                if (vendorCmp !== 0) return vendorCmp;
                return a.invoiceType.localeCompare(b.invoiceType);
            }),
        [vendorFlaggingRules, supplierName],
    );
    const vendorFlaggingPaging = useSubTablePagination(sortedVendorFlaggingRules);

    const isAmountFlaggingEnabled = invoicing.amountMismatchFlaggingEnabled ?? true;
    const flagAmountBothDirections = invoicing.amountMismatchFlagBothDirections ?? true;
    const receivedBaseThreshold =
        invoicing.receivedAmountMismatchBaseDollarThreshold ?? DEFAULT_RECEIVED_BASE_THRESHOLD;
    const creditedBaseThreshold =
        invoicing.returnedAmountMismatchBaseDollarThreshold ?? DEFAULT_RETURNED_BASE_THRESHOLD;
    const receivedPercentThreshold =
        invoicing.receivedAmountMismatchPercentThreshold ?? DEFAULT_RECEIVED_PERCENT_THRESHOLD;
    const creditedPercentThreshold =
        invoicing.returnedAmountMismatchPercentThreshold ?? DEFAULT_RETURNED_PERCENT_THRESHOLD;
    const receivedDollarThreshold = amountMismatchDollarThreshold(
        invoicing.receivedAmountMismatchDollarThreshold,
        receivedBaseThreshold,
    );
    const creditedDollarThreshold = amountMismatchDollarThreshold(
        invoicing.returnedAmountMismatchDollarThreshold,
        creditedBaseThreshold,
        DEFAULT_RETURNED_BASE_THRESHOLD,
    );

    // Adding a rule and the full editor are the same full-screen flow, which
    // owns its own save; 'new' is the add case.
    const openDiscountFlagRule = (ruleKey: string) =>
        navigate(pricingRowPath('invoicing-flags', DISCOUNT_FLAG_SPEC.tabId, ruleKey));

    /**
     * Stage one rule's change. The whole rule is restated — the primary and
     * every sub company on it — so an inline edit keeps the set identical by
     * construction rather than asking the user to repeat it on each company.
     */
    const stageDiscountFlagChange = (
        rule: LkqPricingRule,
        values: Partial<{
            supplierId: string;
            supplierName: string;
            states: string[];
            percentage: number;
        }>,
    ) => {
        const next = {
            supplierId: values.supplierId ?? rule.supplierId,
            supplierName: values.supplierName ?? rule.supplierName,
            make: rule.make,
            states: values.states ?? rule.states,
            percentage: values.percentage ?? rule.percentage,
        };
        // The update payload carries ids only, so a newly picked vendor's name
        // is kept beside the draft to render until the next refetch.
        rememberNames(
            lkqPricingRuleCompanies(rule).map((company) => ({
                insuranceCompanyId: company.id,
                insuranceCompanyName: company.name,
                supplierId: next.supplierId,
                supplierName: next.supplierName,
            })),
        );
        updateInvoicing(
            pricingUpdate(
                DISCOUNT_FLAG_SPEC,
                applyLkqPricingRuleChange(
                    discountFlagExpectations,
                    rule,
                    next,
                    readExpectedDiscount,
                    pricingEntryFactory(DISCOUNT_FLAG_SPEC),
                ),
            ),
        );
    };

    const handleRemoveDiscountFlagRule = (ruleKey: string) => {
        updateInvoicing(
            pricingUpdate(
                DISCOUNT_FLAG_SPEC,
                removeLkqPricingRule(discountFlagExpectations, ruleKey, readExpectedDiscount),
            ),
        );
    };

    const handleRemoveVendorFlaggingRule = (supplierId: string, invoiceType: string) => {
        updateInvoicing({
            vendorFlaggingRules: vendorFlaggingRules.filter(
                (rule) => !(rule.supplierId === supplierId && rule.invoiceType === invoiceType),
            ),
        });
    };

    return (
        <VStack spacing={6} align="stretch" pb={16}>
            <SettingsCard
                title="Mismatch flagging"
                description="Choose which mismatches between the invoice image and Nexsyis data are flagged. Disabling flags increases automation but decreases accuracy."
            >
                <SettingsRow
                    label="Flag quantity mismatches"
                    description="Quantities that do not match the corresponding Nexsyis line (using the Compare quantities setting in General)."
                    control={
                        <Switch
                            size="lg"
                            isChecked={invoicing.quantityMismatchFlaggingEnabled ?? true}
                            onChange={(e) =>
                                updateInvoicing({ quantityMismatchFlaggingEnabled: e.target.checked })
                            }
                            colorScheme="partbay-blue"
                            isDisabled={isSaving}
                        />
                    }
                />
                <SettingsRow
                    label="Flag price mismatches"
                    description="Unit or list prices that do not match Nexsyis."
                    control={
                        <Switch
                            size="lg"
                            isChecked={invoicing.priceMismatchFlaggingEnabled ?? true}
                            onChange={(e) =>
                                updateInvoicing({ priceMismatchFlaggingEnabled: e.target.checked })
                            }
                            colorScheme="partbay-blue"
                            isDisabled={isSaving}
                        />
                    }
                />
                <SettingsRow
                    label="Flag amount mismatches"
                    description="Extended line totals that differ from Nexsyis beyond the thresholds below."
                    control={
                        <Switch
                            size="lg"
                            isChecked={isAmountFlaggingEnabled}
                            onChange={(e) =>
                                updateInvoicing({ amountMismatchFlaggingEnabled: e.target.checked })
                            }
                            colorScheme="partbay-blue"
                            isDisabled={isSaving}
                        />
                    }
                />
            </SettingsCard>

            <Box
                opacity={isAmountFlaggingEnabled ? 1 : 0.5}
                pointerEvents={isAmountFlaggingEnabled ? 'auto' : 'none'}
            >
                <SettingsCard
                    title="Amount mismatch thresholds"
                    description={
                        isAmountFlaggingEnabled
                            ? undefined
                            : 'Amount mismatch flagging is off. Thresholds are preserved but unused until flagging is turned back on.'
                    }
                >
                    <SettingsRow
                        label="Differences to flag"
                        description="Both directions flags a higher or lower invoice total. Invoice higher only flags when the invoice total is above Nexsyis. On credited invoices, that means only a smaller credit. Return slips are not amount-flagged."
                        control={
                            <SearchableSingleSelect
                                options={[...AMOUNT_MISMATCH_DIRECTION_OPTIONS]}
                                value={amountMismatchDirectionValue(flagAmountBothDirections)}
                                onChange={(value) =>
                                    updateInvoicing({
                                        amountMismatchFlagBothDirections:
                                            flagBothDirectionsFromSelection(value),
                                    })
                                }
                                width="280px"
                                isDisabled={isSaving}
                            />
                        }
                    />
                    <SettingsRow
                        label="Received"
                        description={RECEIVED_AMOUNT_THRESHOLD_DESCRIPTION}
                        control={
                            <AmountMismatchThresholdFields
                                idPrefix="Received"
                                baseDollar={receivedBaseThreshold}
                                percent={receivedPercentThreshold}
                                dollar={receivedDollarThreshold}
                                isDisabled={isSaving}
                                onBaseDollarChange={(next) =>
                                    updateInvoicing({ receivedAmountMismatchBaseDollarThreshold: next })
                                }
                                onPercentChange={(next) =>
                                    updateInvoicing({ receivedAmountMismatchPercentThreshold: next })
                                }
                                onDollarChange={(next) =>
                                    updateInvoicing({ receivedAmountMismatchDollarThreshold: next })
                                }
                                trailing={
                                    <InfoTip
                                        ariaLabel="Received amount mismatch threshold example"
                                        label={amountMismatchThresholdHelp(
                                            receivedBaseThreshold,
                                            receivedPercentThreshold,
                                            receivedDollarThreshold,
                                            flagAmountBothDirections,
                                        )}
                                    />
                                }
                            />
                        }
                    />
                    <SettingsRow
                        label="Credited"
                        description={CREDITED_AMOUNT_THRESHOLD_DESCRIPTION}
                        control={
                            <AmountMismatchThresholdFields
                                idPrefix="Credited"
                                baseDollar={creditedBaseThreshold}
                                percent={creditedPercentThreshold}
                                dollar={creditedDollarThreshold}
                                isDisabled={isSaving}
                                onBaseDollarChange={(next) =>
                                    updateInvoicing({ returnedAmountMismatchBaseDollarThreshold: next })
                                }
                                onPercentChange={(next) =>
                                    updateInvoicing({ returnedAmountMismatchPercentThreshold: next })
                                }
                                onDollarChange={(next) =>
                                    updateInvoicing({ returnedAmountMismatchDollarThreshold: next })
                                }
                                trailing={
                                    <InfoTip
                                        ariaLabel="Credited amount mismatch threshold example"
                                        label={amountMismatchThresholdHelp(
                                            creditedBaseThreshold,
                                            creditedPercentThreshold,
                                            creditedDollarThreshold,
                                            flagAmountBothDirections,
                                        )}
                                    />
                                }
                            />
                        }
                    />
                </SettingsCard>
            </Box>

            <SettingsCard
                title="Discount flag"
                description="Flag any invoice where the implied discount is lower than expected."
                headerAction={
                    <PartBayButton
                        colorScheme="partbay-blue"
                        variant="solid"
                        size="sm"
                        text="Add Rule"
                        onClick={() => openDiscountFlagRule('new')}
                        isDisabled={isSaving}
                    />
                }
                dividers={false}
            >
                {/* py matches SettingsRow so the table lines up with the rows
                    of the cards around it. */}
                <Box py={4}>
                    <SettingsSubTable
                        isInset
                        currentPage={discountFlagPaging.currentPage}
                        totalPages={discountFlagPaging.totalPages}
                        onPageChange={discountFlagPaging.setCurrentPage}
                        head={
                            <>
                                <SettingsTh>Insurance company</SettingsTh>
                                <SettingsTh minW="160px">Vendor name</SettingsTh>
                                <SettingsTh minW="120px">Make</SettingsTh>
                                <SettingsTh minW="160px">States</SettingsTh>
                                <SettingsTh minW="140px">Expected % discount</SettingsTh>
                                <SettingsTh textAlign="center" w="100px">
                                    Actions
                                </SettingsTh>
                            </>
                        }
                    >
                        {discountFlagRules.length === 0 ? (
                            <Tr>
                                <SettingsTd colSpan={6} py={6} color="gray.500">
                                    No discount flag expectations configured yet.
                                </SettingsTd>
                            </Tr>
                        ) : (
                            discountFlagPaging.pageItems.map((rule) => (
                                // role="group" powers the chevron that fades in
                                // on hover for every editable cell in the row.
                                <Tr key={rule.ruleKey} role="group" _hover={{ bg: 'gray.25' }}>
                                    <SettingsTd>
                                        {/* The company name is the way into the
                                            full editor; every other column edits
                                            in place. A branched sub company
                                            carries its parent in front of it, and
                                            a primary lists what rides along. */}
                                        <VStack align="flex-start" spacing={0.5}>
                                            <HStack spacing={1} minW={0}>
                                                {rule.parentInsuranceCompanyName && (
                                                    <>
                                                        <Text
                                                            fontSize="sm"
                                                            color="gray.500"
                                                            noOfLines={1}
                                                        >
                                                            {rule.parentInsuranceCompanyName}
                                                        </Text>
                                                        <Icon
                                                            as={TbChevronRight as ElementType}
                                                            boxSize={3.5}
                                                            color="gray.400"
                                                            flexShrink={0}
                                                        />
                                                    </>
                                                )}
                                                <Text
                                                    as="button"
                                                    fontSize="sm"
                                                    textAlign="left"
                                                    color="gray.800"
                                                    _hover={{
                                                        color: PARTBAY_BLUE,
                                                        textDecoration: 'underline',
                                                    }}
                                                    onClick={() =>
                                                        openDiscountFlagRule(rule.ruleKey)
                                                    }
                                                >
                                                    {rule.insuranceCompanyName}
                                                </Text>
                                            </HStack>
                                            {rule.includedCompanies.length > 0 && (
                                                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                                    {formatLkqPricingSubCompanySummary(rule)}
                                                </Text>
                                            )}
                                        </VStack>
                                    </SettingsTd>
                                    <SettingsTd>
                                        <InlinePricingVendorCell
                                            value={{
                                                supplierId: rule.supplierId,
                                                supplierName: rule.supplierName,
                                            }}
                                            ariaLabel={`Edit ${rule.insuranceCompanyName} vendor`}
                                            isDisabled={isSaving}
                                            onSave={(next) =>
                                                stageDiscountFlagChange(rule, next)
                                            }
                                        />
                                    </SettingsTd>
                                    <SettingsTd>
                                        <Text fontSize="sm" color="gray.700" textTransform="capitalize">
                                            {rule.make || 'All'}
                                        </Text>
                                    </SettingsTd>
                                    <SettingsTd>
                                        <InlinePricingStatesCell
                                            value={rule.states}
                                            options={organizationStates}
                                            ariaLabel={`Edit ${rule.insuranceCompanyName} states`}
                                            isDisabled={isSaving}
                                            onSave={(states) =>
                                                stageDiscountFlagChange(rule, { states })
                                            }
                                        />
                                    </SettingsTd>
                                    <SettingsTd>
                                        <SettingsInlineNumberEdit
                                            display={
                                                <Text fontSize="sm" color="gray.700">
                                                    {decimalToWholePercent(rule.percentage)}%
                                                </Text>
                                            }
                                            value={decimalToWholePercent(rule.percentage)}
                                            unit="%"
                                            min={0}
                                            max={100}
                                            placeholder="0"
                                            // A percentage needs a couple of
                                            // characters, so the editor stays
                                            // narrow instead of matching the cell.
                                            width="150px"
                                            minW="150px"
                                            isDisabled={isSaving}
                                            ariaLabel={`Edit ${rule.insuranceCompanyName} expected discount`}
                                            onSave={(next) => {
                                                // Clearing the field would leave
                                                // a rule with nothing to expect,
                                                // so an empty entry is ignored.
                                                if (next === null) return;
                                                stageDiscountFlagChange(rule, {
                                                    percentage: wholePercentToDecimal(next),
                                                });
                                            }}
                                        />
                                    </SettingsTd>
                                    <SettingsTd textAlign="center">
                                        <SettingsRowActions
                                            onEdit={() => openDiscountFlagRule(rule.ruleKey)}
                                            onDelete={() =>
                                                handleRemoveDiscountFlagRule(rule.ruleKey)
                                            }
                                            editLabel="Edit discount flag rule"
                                            deleteLabel="Remove discount flag rule"
                                            isEditDisabled={isSaving}
                                            isDeleteDisabled={isSaving}
                                        />
                                    </SettingsTd>
                                </Tr>
                            ))
                        )}
                    </SettingsSubTable>
                </Box>
            </SettingsCard>

            <SettingsCard
                title="Vendor flagging rules"
                description="Override org-wide unmatched-submit and mismatch flagging for a specific vendor and invoice type."
                headerAction={
                    <PartBayButton
                        variant="solid"
                        colorScheme="partbay-blue"
                        onClick={() => navigate(vendorFlaggingRulePath('new'))}
                        text="Add Rule"
                        size="sm"
                        isDisabled={isSaving}
                    />
                }
                dividers={false}
            >
                <Box py={4}>
                    <SettingsSubTable
                        isInset
                        currentPage={vendorFlaggingPaging.currentPage}
                        totalPages={vendorFlaggingPaging.totalPages}
                        onPageChange={vendorFlaggingPaging.setCurrentPage}
                        head={
                            <>
                                <SettingsTh minW="160px">Vendor</SettingsTh>
                                <SettingsTh minW="120px">Invoice type</SettingsTh>
                                <SettingsTh>Overrides</SettingsTh>
                                <SettingsTh textAlign="center" w="100px">
                                    Actions
                                </SettingsTh>
                            </>
                        }
                    >
                        {sortedVendorFlaggingRules.length === 0 ? (
                            <Tr>
                                <SettingsTd colSpan={4} py={6} color="gray.500">
                                    No vendor flagging rules configured yet.
                                </SettingsTd>
                            </Tr>
                        ) : (
                            vendorFlaggingPaging.pageItems.map((rule) => (
                                <Tr
                                    key={vendorFlaggingRuleKey(rule.supplierId, rule.invoiceType)}
                                    role="group"
                                    _hover={{ bg: 'gray.25' }}
                                >
                                    <SettingsTd>
                                        <Text
                                            as="button"
                                            fontSize="sm"
                                            textAlign="left"
                                            color="gray.800"
                                            fontWeight="medium"
                                            _hover={{
                                                color: PARTBAY_BLUE,
                                                textDecoration: 'underline',
                                            }}
                                            onClick={() =>
                                                navigate(
                                                    vendorFlaggingRulePath(
                                                        vendorFlaggingRuleKey(
                                                            rule.supplierId,
                                                            rule.invoiceType,
                                                        ),
                                                    ),
                                                )
                                            }
                                        >
                                            {supplierName(rule.supplierId)}
                                        </Text>
                                    </SettingsTd>
                                    <SettingsTd>
                                        {vendorFlaggingInvoiceTypeLabel(rule.invoiceType)}
                                    </SettingsTd>
                                    <SettingsTd>
                                        <Text fontSize="sm" color="gray.600">
                                            {vendorFlaggingRuleSummary(rule)}
                                        </Text>
                                    </SettingsTd>
                                    <SettingsTd textAlign="center">
                                        <SettingsRowActions
                                            onEdit={() =>
                                                navigate(
                                                    vendorFlaggingRulePath(
                                                        vendorFlaggingRuleKey(
                                                            rule.supplierId,
                                                            rule.invoiceType,
                                                        ),
                                                    ),
                                                )
                                            }
                                            onDelete={() =>
                                                handleRemoveVendorFlaggingRule(
                                                    rule.supplierId,
                                                    rule.invoiceType,
                                                )
                                            }
                                            editLabel={`Edit ${supplierName(rule.supplierId)} flagging rule`}
                                            deleteLabel={`Remove ${supplierName(rule.supplierId)}`}
                                            isEditDisabled={isSaving}
                                            isDeleteDisabled={isSaving}
                                        />
                                    </SettingsTd>
                                </Tr>
                            ))
                        )}
                    </SettingsSubTable>
                </Box>
            </SettingsCard>
        </VStack>
    );
};

export default FlagsSection;
